import { ReactNode } from "react";

import AppLink from "@components/link/link";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { getAssetUrl } from "@helpers/helpers";

export const ORGANIZATION_INTEGRATION_ADMIN_TOOLTIP =
  "Contact your organization admin to enable/disable organization integrations";

type StatusTone = "enabled" | "neutral" | "warning";

const STATUS_CLASS: Record<StatusTone, string> = {
  enabled: "text-emerald-600",
  neutral: "text-gray-500",
  warning: "text-amber-600",
};

type CommonProps = {
  name: string;
  logo: string;
  logoNode?: ReactNode;
  summary: ReactNode;
  statusLabel?: string;
  statusTone?: StatusTone;
  isLoading?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
};

type LinkProps = CommonProps & { to: string; onClick?: never };
type ButtonProps = CommonProps & { to?: never; onClick: () => void };

type IntegrationCardProps = LinkProps | ButtonProps;

const CardBody = ({
  name,
  logo,
  logoNode,
  summary,
  statusLabel,
  statusTone = "neutral",
}: CommonProps) => (
  <>
    <div className="flex items-center gap-3">
      <div className="h-7 w-7 shrink-0 flex items-center justify-center">
        {logoNode ?? (
          <img
            src={getAssetUrl(logo)}
            alt=""
            className="h-full w-full object-contain"
          />
        )}
      </div>
      <div className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">
        {name}
      </div>
      {statusLabel &&
        (statusTone === "neutral" ? (
          <span className="shrink-0 inline-flex items-center gap-0.5 rounded-sm px-1.5 py-0.5 text-xs font-medium text-blue-500 hover:bg-blue-100 hover:text-blue-700">
            {statusLabel}
            <TopicflowIcon type="chevronRight" className="h-3.5 w-3.5" />
          </span>
        ) : (
          <span
            className={classNames(
              "shrink-0 text-xs font-medium",
              STATUS_CLASS[statusTone],
            )}
          >
            {statusLabel}
          </span>
        ))}
    </div>
    <div className="text-sm text-gray-500 mt-3 line-clamp-3">{summary}</div>
  </>
);

const CARD_CLASS =
  "flex flex-col text-left bg-white rounded-lg border border-gray-200 p-4 hover:bg-gray-50 hover:border-gray-300 hover:shadow-xs";

const IntegrationCard = (props: IntegrationCardProps) => {
  if ("to" in props && props.to) {
    return (
      <AppLink to={props.to} className={CARD_CLASS}>
        <CardBody {...props} />
      </AppLink>
    );
  }
  const button = (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.isLoading || props.isDisabled}
      className={classNames(CARD_CLASS, "w-full disabled:cursor-not-allowed")}
    >
      <CardBody {...props} />
    </button>
  );

  if (props.isDisabled && props.disabledTooltip) {
    return (
      <Tooltip text={props.disabledTooltip}>
        <span className="block h-full">{button}</span>
      </Tooltip>
    );
  }

  return button;
};

export default IntegrationCard;
